import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../client";
import type { RevokeLeavePreview } from "./reversal";

// Leave-type codes are DB-managed (Technical Admin CRUD) — no longer a fixed
// union. Kept as a loose string alias.
export type LeaveType = string;

export type LeaveDurationKind = "full_day" | "half_day" | "partial";

export type LeaveStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "cancelled"
  | "revoked";

export type LeaveScope = "self" | "team" | "all";

export type LeaveStage = "head" | "hr";

export type HeadStageSkipReason =
  | "requester_is_head"
  | "no_primary_dept"
  | "preempted_by_global"
  | "";

export interface LeaveRow {
  id: string;
  userId: string;
  userName: string | null;
  userEmail: string | null;
  leaveType: LeaveType;
  leaveTypeLabel: string;
  leaveTypeTone: string;
  durationKind: LeaveDurationKind;
  startsOn: string;
  endsOn: string;
  partialStartTime: string | null;
  partialEndTime: string | null;
  totalDays: number;
  reason: string;
  attachmentId: string | null;
  attachmentFilename: string | null;
  attachmentContentType: string | null;
  status: LeaveStatus;
  currentStage: LeaveStage | null;
  primaryDepartmentId: string | null;
  primaryDepartmentName: string | null;
  headApprovedById: string | null;
  headApprovedByName: string | null;
  headApproverRoleAtTime: string;
  headApprovedAt: string | null;
  headApprovalNote: string;
  headStageSkippedReason: HeadStageSkipReason;
  hasAttendanceConflict: boolean;
  resolvedById: string | null;
  resolvedByName: string | null;
  resolverRoleAtTime: string;
  resolvedAt: string | null;
  resolutionNote: string;
  cancelledById: string | null;
  cancelledByName: string | null;
  cancelledAt: string | null;
  cancellationNote: string;
  revokedById: string | null;
  revokedByName: string | null;
  revokedAt: string | null;
  revocationReason: string;
  canRevoke: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface LeaveListResponse {
  results: LeaveRow[];
  nextCursor: string | null;
  prevCursor: string | null;
}

export interface LeaveCreateInput {
  leaveType: LeaveType;
  durationKind: LeaveDurationKind;
  startsOn: string;
  endsOn: string;
  partialStartTime?: string | null;
  partialEndTime?: string | null;
  reason: string;
  attachmentId: string;
}

const listKey = (params: object) => ["leaves", "list", params] as const;

export function useLeaves(params: {
  scope?: LeaveScope;
  status?: LeaveStatus | "";
  leaveType?: LeaveType | "";
  userId?: string;
  from?: string;
  to?: string;
} = {}) {
  return useQuery<LeaveListResponse>({
    queryKey: listKey(params),
    queryFn: () =>
      api
        .get<LeaveListResponse>("/leaves", {
          params: {
            scope: params.scope || "self",
            status: params.status || undefined,
            leaveType: params.leaveType || undefined,
            userId: params.userId || undefined,
            from: params.from || undefined,
            to: params.to || undefined,
          },
        })
        .then((r) => r.data),
  });
}

export function useCreateLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: LeaveCreateInput) =>
      api.post<LeaveRow>("/leaves", input).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leaves"] }),
  });
}

export function useApproveLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) =>
      api.post<LeaveRow>(`/leaves/${id}/approve`, { note: note ?? "" }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leaves"] }),
  });
}

export function useRejectLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      api.post<LeaveRow>(`/leaves/${id}/reject`, { note }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leaves"] }),
  });
}

export function useCancelLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) =>
      api.post<LeaveRow>(`/leaves/${id}/cancel`, { note: note ?? "" }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leaves"] }),
  });
}

/* ------------------------------- reversal (SA/TA) ------------------------------ */

/** Dry run — what revoking this approved leave would do. No writes. */
export function useRevokeLeavePreview(id: string | null, enabled = true) {
  return useQuery<RevokeLeavePreview>({
    queryKey: ["leaves", "revokePreview", id],
    queryFn: () =>
      api.get<RevokeLeavePreview>(`/leaves/${id}/revoke`).then((r) => r.data),
    enabled: enabled && !!id,
  });
}

export function useRevokeLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post<LeaveRow>(`/leaves/${id}/revoke`, { reason }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leaves"] }),
  });
}
